import { X } from 'lucide-react'
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { useFetcher } from 'react-router'
import { twMerge } from 'tailwind-merge'
import type { loader as zipSearchLoader } from '../routes/resource.zip-search'

type Selected = { zip: string; city: string | null; state: string | null }

type ZipComboboxProps = {
  /** Form-control name — the hidden input carries the selected ZIP under this name. */
  name: string
  label?: string | undefined
  /** Classes for the `<label>`. Pass `sr-only` where the design has no visible
   *  label text — the control keeps a real label either way. */
  labelClassName?: string | undefined
  /** Merged over the input's base classes (via twMerge), so a host can restyle
   *  the field without forking the component. */
  inputClassName?: string | undefined
  placeholder?: string | undefined
  /** Currently-selected ZIP. Pass `null` to clear. */
  value: Selected | null
  onChange: (next: Selected | null) => void
  /** Marks the visible input required so the native form blocks empty submits. */
  required?: boolean | undefined
  'data-testid'?: string | undefined
}

const DEBOUNCE_MS = 300
// Match the resource loader's guard so the dropdown doesn't flash for 0–1 chars.
const MIN_QUERY_LENGTH = 2

// Render a suggestion as `ZIP — City, State`, falling back to the bare ZIP when
// city/state are unknown (e.g. a URL-seeded value carries only the ZIP).
const formatLabel = ({ zip, city, state }: Selected): string => {
  const location = [city, state].filter(Boolean).join(', ')
  return location !== '' ? `${zip} — ${location}` : zip
}

/**
 * ZIP-code type-ahead for the Atlas Web provider search. The visitor types a ZIP
 * prefix, picks from suggestions (`ZIP — City, State`), and the form submits the
 * bare ZIP under the hood via a hidden input.
 *
 * Cloned from `SpecialtyCombobox`, kept independent by design (VIT-17093). Two
 * Atlas Web adaptations carried over from the specialty control:
 *
 *  - Data path: Atlas Web's `providersClient` is server-only (SigV4-signed) and
 *    can't be called from the browser, so instead of a direct client call this
 *    loads the `/resource/zip-search` resource route via `useFetcher`
 *    (loader-only, runs server-side). React Router cancels superseded fetches, so
 *    it also handles the stale-response ordering.
 *  - Styling: raw Tailwind + CSS vars matching the sibling inputs on the home
 *    form.
 *
 * ZIP is a single-source lookup (no mapping merge/dedupe) and human-readable, so
 * a URL-seeded value needs no resolve round-trip — it renders as the bare ZIP.
 */
export const ZipCombobox = ({
  name,
  label,
  labelClassName = undefined,
  inputClassName = undefined,
  placeholder = 'e.g. 97201',
  value,
  onChange,
  required = false,
  'data-testid': dataTestId,
}: ZipComboboxProps) => {
  const [query, setQuery] = useState(value != null ? formatLabel(value) : '')
  const [open, setOpen] = useState(false)
  // Index of the keyboard-highlighted suggestion; -1 means none highlighted, so
  // the first ArrowDown lands on item 0.
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const inputId = useId()
  // Stable ids for the ARIA combobox↔listbox↔option wiring.
  const listboxId = useId()
  const optionId = (index: number) => `${listboxId}-option-${index}`
  // useId() emits colons, which aren't valid in CSS anchor-name / position-anchor
  // identifiers — strip them so the anchor positioning binds (mirrors health's
  // Dropdown). Each instance gets its own anchor so multiple comboboxes don't
  // collide.
  const anchorName = `--zip-anchor-${useId().replace(/:/g, '')}`
  // Distinguish "we cleared the value as a side effect of typing" (no resync)
  // from "parent externally changed the value" (resync).
  const skipNextValueSyncRef = useRef(false)

  const fetcher = useFetcher<typeof zipSearchLoader>()
  const loading = fetcher.state !== 'idle'

  // `useFetcher` keeps the previous response until the next debounced reload
  // resolves, so right after the prefix changes it still holds the old query's
  // rows. This is a prefix search — every fresh row's zip starts with the queried
  // leading digits (the same normalization the backend applies) — so drop any row
  // that no longer matches the current input. Without this, a quick ArrowDown +
  // Enter during the debounce window could commit a stale ZIP that doesn't match
  // what's typed (e.g. switching a `941` prefix to `972`).
  const queryDigits = query.trim().match(/^\d+/)?.[0] ?? ''
  const suggestions = (fetcher.data?.items ?? []).filter((item) => item.zip.startsWith(queryDigits))

  // The formatted label for the committed selection, or '' when none. Used both
  // as the visible text and as the "is the query still the committed value?"
  // comparison so refocus/hydration stay consistent.
  const selectedLabel = value != null ? formatLabel(value) : ''

  // Sync the visible text whenever the parent flips the selected value
  // (e.g. clearing from outside, hydrating from a saved search).
  useEffect(() => {
    if (skipNextValueSyncRef.current) {
      skipNextValueSyncRef.current = false
      return
    }
    setQuery(selectedLabel)
  }, [selectedLabel])

  // Debounce the lookup as the user types. Skip the fetch right after a
  // selection (query already equals the selected label) and for trivial queries.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fetcher identity is stable; depending on it would re-run the debounce on every fetcher state transition.
  useEffect(() => {
    if (value != null && query === selectedLabel) return
    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) return

    const handle = setTimeout(() => {
      void fetcher.load(`/resource/zip-search?prefix=${encodeURIComponent(trimmed)}`)
    }, DEBOUNCE_MS)
    return () => {
      clearTimeout(handle)
    }
  }, [query, value, selectedLabel])

  // Close the suggestion list on outside click.
  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
    }
  }, [])

  // Whether the visible text still matches a committed selection. In that state
  // there's nothing to pick, and the debounce deliberately skips re-fetching — so
  // `useFetcher` still holds the *previous* query's suggestions. Suppress the
  // dropdown here so refocusing a completed field doesn't reopen those stale rows
  // (which could otherwise swap the hidden ZIP to one that doesn't match the shown
  // text). As soon as the user edits, `value` is nulled and a fresh fetch runs,
  // so the dropdown reappears with matching results.
  const matchesCommittedSelection = value != null && query === selectedLabel

  // Only surface the dropdown for non-trivial queries so a stale fetcher result
  // doesn't reappear once the visitor deletes back below the minimum.
  const showDropdown =
    open && !matchesCommittedSelection && query.trim().length >= MIN_QUERY_LENGTH && (loading || suggestions.length > 0)

  // The suggestion list is a native popover (top layer + CSS anchor positioning),
  // so it escapes the form's stacking/overflow context and floats over the page.
  // A text input can't be a `popovertarget` invoker (only buttons can), so drive
  // show/hide imperatively from our own open state. Guard on :popover-open since
  // showPopover()/hidePopover() throw if called in the wrong state.
  useEffect(() => {
    const el = popoverRef.current
    if (!el) return
    try {
      if (showDropdown && !el.matches(':popover-open')) {
        el.showPopover()
      } else if (!showDropdown && el.matches(':popover-open')) {
        el.hidePopover()
      }
    } catch {
      // Popover API unavailable / element not connected — degrade to no dropdown.
    }
  }, [showDropdown])

  // Drop the highlight whenever the dropdown is hidden so it doesn't reappear
  // stale on the next open. (Typing also resets it in onChange, and a new result
  // set only ever follows typing, so ArrowDown always starts from the top.)
  useEffect(() => {
    if (!showDropdown) setActiveIndex(-1)
  }, [showDropdown])

  // `required` only blocks a fully-empty input; typed free text with no chosen
  // suggestion would otherwise submit an empty hidden ZIP and silently no-op the
  // search. Flag the input invalid whenever there's text but no selection so the
  // native form validation blocks submit with a clear message.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    const typedWithoutSelection = query.trim() !== '' && value == null
    el.setCustomValidity(typedWithoutSelection ? 'Please select a ZIP code from the list.' : '')
  }, [query, value])

  // Keep the highlighted row scrolled into view as the user arrows through a list
  // taller than the popover.
  useEffect(() => {
    if (activeIndex < 0) return
    const items = popoverRef.current?.querySelectorAll('[role="option"]')
    items?.[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const handleSelect = (suggestion: Selected) => {
    onChange({ zip: suggestion.zip, city: suggestion.city, state: suggestion.state })
    setQuery(formatLabel(suggestion))
    setOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setQuery('')
    setOpen(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        // Open on first ArrowDown if there are results but the list is closed.
        if (!showDropdown) {
          setOpen(true)
          return
        }
        event.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1))
        break
      case 'ArrowUp':
        if (!showDropdown) return
        event.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
        break
      case 'Enter': {
        // Select the highlighted row instead of submitting the form. With nothing
        // highlighted, let Enter fall through to the native form submit.
        const active = activeIndex >= 0 ? suggestions[activeIndex] : undefined
        if (active) {
          event.preventDefault()
          handleSelect(active)
        }
        break
      }
      case 'Escape':
        if (showDropdown) {
          event.preventDefault()
          setOpen(false)
          setActiveIndex(-1)
        }
        break
    }
  }

  return (
    <div ref={containerRef} className="relative flex flex-col w-full" data-testid={dataTestId}>
      {label != null && (
        <label htmlFor={inputId} className={labelClassName}>
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          // ARIA combobox wiring so screen readers announce the suggestions, the
          // expanded state, and which option is highlighted.
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          // `required` blocks a fully-empty input; the setCustomValidity effect
          // above additionally blocks submit when there's typed text but no chosen
          // suggestion (which would post an empty hidden ZIP).
          required={required}
          // twMerge so a host's `inputClassName` replaces conflicting base
          // utilities rather than fighting them on specificity.
          className={twMerge('w-full bg-white border py-2 px-3 pr-9 rounded', inputClassName)}
          // anchor-name binds this input as the positioning anchor for the popover.
          // Set via style (not a Tailwind class) since the anchor id is computed at
          // runtime and Tailwind can only emit classes it sees as literals at build.
          style={{ anchorName }}
          placeholder={placeholder}
          value={query}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            // Typing invalidates the current highlight — start fresh.
            setActiveIndex(-1)
            // Typing edits the in-flight selection — reflect that to the parent
            // immediately so it doesn't keep stale data. Mark the next value-sync
            // as a side effect of our own onChange so the effect doesn't wipe what
            // the user just typed.
            if (value != null && e.target.value !== selectedLabel) {
              skipNextValueSyncRef.current = true
              onChange(null)
            }
          }}
          onFocus={() => {
            setOpen(true)
          }}
          data-testid={dataTestId ? `${dataTestId}-input` : undefined}
        />
        {/* Hidden field carries the selected ZIP for the GET form submit. */}
        <input type="hidden" name={name} value={value?.zip ?? ''} />
        {(value != null || query !== '') && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 inline-flex items-center justify-center cursor-pointer"
            aria-label="Clear zip code"
            data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
          >
            <X className="size-4 text-slate-500" />
          </button>
        )}
      </div>
      {/* Native popover (manual): rendered in the top layer and positioned by CSS
          anchor positioning against the input, so it floats above the page instead
          of being clipped by the form. Shown/hidden imperatively via the effect
          above. `inset-auto` + explicit margin override the UA-centered default. */}
      {/* Native popover (manual) as the ARIA listbox. A div (not ul/li) carries the
          listbox/option roles so screen readers announce the suggestions; focus
          stays in the input and the active option is tracked via
          aria-activedescendant. */}
      <div
        ref={popoverRef}
        id={listboxId}
        role="listbox"
        popover="manual"
        className="inset-auto [margin:0.25rem_0_0_0] [position-area:bottom_span-right] [position-try-fallbacks:flip-block] w-[anchor-size(width)] max-h-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
        // position-anchor points at the input's anchor-name; set via style for the
        // same runtime-id reason as the input's anchor-name above.
        style={{ positionAnchor: anchorName }}
        data-testid={dataTestId ? `${dataTestId}-suggestions` : undefined}
      >
        {loading && suggestions.length === 0 && <div className="px-3 py-2 text-slate-500">Searching…</div>}
        {suggestions.map((suggestion, index) => (
          <button
            key={suggestion.zip}
            type="button"
            id={optionId(index)}
            role="option"
            aria-selected={index === activeIndex}
            onClick={() => {
              handleSelect(suggestion)
            }}
            // Hovering syncs the keyboard highlight so mouse and keyboard don't
            // fight over which row is active.
            onMouseEnter={() => {
              setActiveIndex(index)
            }}
            className={`block w-full text-left px-3 py-2 transition-colors cursor-pointer ${
              index === activeIndex ? 'bg-primary-500 text-white' : 'hover:bg-secondary-100'
            }`}
            data-testid={dataTestId ? `${dataTestId}-suggestion-${suggestion.zip}` : undefined}
          >
            {/* Shows `ZIP — City, State` (or the bare ZIP when city/state are
                unknown). The active row inherits the button's white text. */}
            <span className={`truncate font-medium ${index === activeIndex ? 'text-white' : 'text-slate-900'}`}>
              {formatLabel(suggestion)}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
